import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation, useHistory } from "react-router-dom";
import usePayAxios from "../../config/useAxios";
import { apis } from "../../config/APIs";
import {
  initialisePublicKeyData,
  OBM_EncryptPassword_Ex,
  OBM_GetEncodingParameter,
  OBM_GetEncryptedPassword,
} from "./pinEncryption";
import { message } from "antd";

/* ---------------- Encryption Helpers ---------------- */

const generateEncrptionPin = (modulus, exp, pin) => {
  const hash = "SHA2-256";
  return {
    ...getNewEncryptedPIN(modulus, exp, pin, hash),
    ...getCurrentEncryptedPIN(modulus, exp, pin, hash),
  };
};

function getRandomString(length) {
  const chars = "0123456789";
  let result = "";
  for (let i = length; i > 0; --i) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}

function getNewEncryptedPIN(mod, exp, pin, hash) {
  const rnd = getRandomString(16);
  let res = initialisePublicKeyData(mod, exp);
  if (res === 0) res = OBM_EncryptPassword_Ex(pin, rnd, hash);

  if (res === 0) {
    return {
      new_enc_pin: OBM_GetEncryptedPassword(),
      new_pkcs_one_param: OBM_GetEncodingParameter(),
      new_random_string: rnd,
    };
  }
  return {};
}

function getCurrentEncryptedPIN(mod, exp, pin, hash) {
  const rnd = getRandomString(16);
  let res = initialisePublicKeyData(mod, exp);
  if (res === 0) res = OBM_EncryptPassword_Ex(pin, rnd, hash);

  if (res === 0) {
    return {
      current_enc_pin: OBM_GetEncryptedPassword(),
      current_pkcs_one_param: OBM_GetEncodingParameter(),
      current_random_string: rnd,
    };
  }
  return {};
}

/* ---------------- Validation ---------------- */

const validationSchema = Yup.object().shape({
  cardNumber: Yup.string().required("Card number is required"),
  otp: Yup.string()
    .matches(/^[0-9]{6}$/, "OTP must be a 6 Digit Numeric Value only")
    .required("OTP is required"),
  pin: Yup.string()
    .matches(/^[0-9]{4}$/, "PIN must be exactly 4 digits")
    .required("PIN is required"),
  confirmPin: Yup.string()
    .oneOf([Yup.ref("pin"), null], "PINs must match")
    .required("Confirm PIN is required"),
});

/* ---------------- Component ---------------- */

const SetPin = () => {
  const { onCall: setPinCard, loading } = usePayAxios({
    api: apis.card_pin_set,
    method: "post",
  });

  const { onCall: resentOtp } = usePayAxios({
    api: apis.pinset_resend_otp,
    method: "post",
  });

  const history = useHistory();
  const location = useLocation();
  const { card_id, card_number, pinMeta, cardData, isVertical} = location.state || {};

  const [timer, setTimer] = useState(120);
  const interval = useRef(null);

  /* -------- Guard -------- */
  useEffect(() => {
    if (!pinMeta?.modulus || !pinMeta?.exponent || !card_id) {
      message.error(
        <span className="messageText">
          Invalid PIN request. Please try again.
        </span>,
      );
      history.replace("/cards");
      return;
    }
    startTimer();
    return () => clearInterval(interval.current);
  }, []);

  const startTimer = () => {
    interval.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec < 10 ? `0${sec}` : sec}`;
  };

  /* -------- Resend OTP -------- */
  const handleResendOTP = () => {
    resentOtp({ data: { card_id } })
      .then((res) => {
        if (res?.response?.otpExpiryInMinutes) {
          setTimer(120);
          startTimer();
        }
      })
      .catch((error) => {
        const apiError = error?.response?.data;
      });
  };

  /* -------- Formik -------- */
  const formik = useFormik({
    initialValues: {
      cardNumber: card_number || "",
      otp: "",
      pin: "",
      confirmPin: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const encObj = generateEncrptionPin(
        pinMeta.modulus,
        pinMeta.exponent,
        values.confirmPin,
      );

      setPinCard({
        data: {
          card_id,
          otp: values.otp,
          src_req_id: pinMeta.srcReqId,
          ...encObj,
        },
      })
        .then(() => {
            history.push("/card-details/" + card_id, {
              pinSuccess: true,
              cardData: cardData,       
              isVertical: isVertical, 
            });
        })
        .catch((error) => {
          const apiError = error?.response?.data;
          if (apiError?.errors === "Invalid OTP") {
            formik.setFieldError("otp", "Invalid OTP");
            return;
          }
        });
    },
  });

  return (
    <div className="set-pin">
      <div>
        <h4>Set PIN/Reset PIN</h4>

        <div className="text-center mb-2">
          {timer > 0 && (
            <>
              <strong>Remaining time {formatTime(timer)}</strong>
              <br />
            </>
          )}

          <span className={pinMeta?.modulus ? "text-success" : "text-error"}>
            {pinMeta?.respMsg?.replace("Success:", "")}
          </span>
        </div>

        <form onSubmit={formik.handleSubmit}>
          {/* Card Number */}
          <div className="item">
            <label className="form-label">Card Number</label>
            <input
              type="text"
              className="form-control"
              name="cardNumber"
              value={formik.values.cardNumber}
              disabled
            />
          </div>

          {/* OTP */}
          <div className="item">
            <label className="form-label">OTP</label>
            <input
              type="text"
              className="form-control"
              name="otp"
              maxLength={6}
              value={formik.values.otp}
              onChange={(e) =>
                formik.setFieldValue("otp", e.target.value.replace(/\D/g, ""))
              }
            />
            {formik.errors.otp && (
              <div className="text-error small">{formik.errors.otp}</div>
            )}
          </div>

          {/* PIN */}
          <div className="item">
            <label className="form-label">Set PIN</label>
            <input
              type="password"
              className="form-control"
              name="pin"
              maxLength={4}
              value={formik.values.pin}
              onChange={(e) =>
                formik.setFieldValue("pin", e.target.value.replace(/\D/g, ""))
              }
              onBlur={formik.handleBlur}
            />
            {formik.touched.pin && formik.errors.pin && (
              <div className="text-error small">{formik.errors.pin}</div>
            )}
          </div>

          {/* Confirm PIN */}
          <div className="item">
            <label className="form-label">Confirm PIN</label>
            <input
              type="password"
              className="form-control"
              name="confirmPin"
              maxLength={4}
              value={formik.values.confirmPin}
              onChange={(e) =>
                formik.setFieldValue(
                  "confirmPin",
                  e.target.value.replace(/\D/g, ""),
                )
              }
              onBlur={formik.handleBlur}
            />
            {formik.touched.confirmPin && formik.errors.confirmPin && (
              <div className="text-error small">{formik.errors.confirmPin}</div>
            )}
          </div>

          {/* Buttons */}
          <div className="d-flex justify-content-between mt2">
            <button type="submit" className="primary-btn" disabled={loading}>
              Submit
            </button>

            {/* RESET BUTTON — unchanged */}
            <button
              type="button"
              className="nm-btn ml1"
              onClick={() => {
                formik.resetForm({
                  values: {
                    cardNumber: formik.values.cardNumber,
                    otp: "",
                    pin: "",
                    confirmPin: "",
                  },
                });
              }}
            >
              Reset
            </button>

            {timer === 0 && (
              <button
                className="primary-btn"
                type="button"
                onClick={handleResendOTP}
              >
                Resend OTP
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetPin;
